import React, { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { FiUploadCloud, FiX, FiImage } from 'react-icons/fi';
import { validateImageFile, formatFileSize, compressImage } from '../../utils/helpers';
import toast from 'react-hot-toast';

const ImageUpload = ({ onImageSelect, currentImage, error }) => {
  const [preview, setPreview] = useState(currentImage || null);
  const [isCompressing, setIsCompressing] = useState(false);

  const onDrop = useCallback(async (acceptedFiles) => {
    const file = acceptedFiles[0];
    
    try {
      validateImageFile(file);
      setIsCompressing(true);
      
      // Compress the image
      const compressedFile = await compressImage(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result);
      };
      reader.readAsDataURL(compressedFile);
      
      // Call parent callback with compressed file
      onImageSelect(compressedFile);
      
      toast.success(`Image compressed from ${formatFileSize(file.size)} to ${formatFileSize(compressedFile.size)}`);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsCompressing(false);
    }
  }, [onImageSelect]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.webp']
    },
    maxFiles: 1,
    multiple: false
  });

  const removeImage = () => {
    setPreview(null);
    onImageSelect(null);
  };

  return (
    <div className="image-upload-container">
      {!preview ? (
        <div
          {...getRootProps()}
          className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors
            ${isDragActive ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-gray-400'}
            ${error ? 'border-red-500' : ''}`}
        >
          <input {...getInputProps()} />
          <FiUploadCloud className="mx-auto text-4xl text-gray-400 mb-3" />
          {isCompressing ? (
            <p className="text-gray-600">Duke kompresuar imazhin...</p>
          ) : (
            <>
              <p className="text-gray-600 mb-1">
                {isDragActive ? 'Hidhe imazhin këtu' : 'Zvarrit dhe lësho një imazh këtu, ose kliko për të zgjedhur'}
              </p>
              <p className="text-sm text-gray-500">
                Mbështet: JPEG, PNG, WebP (Maksimum 5MB)
              </p>
            </>
          )}
        </div>
      ) : (
        <div className="relative">
          <img
            src={preview}
            alt="Preview"
            className="w-full h-64 object-cover rounded-lg"
          />
          <button
            type="button"
            onClick={removeImage}
            className="absolute top-2 right-2 bg-red-500 text-white p-2 rounded-full hover:bg-red-600 transition-colors"
          >
            <FiX />
          </button>
          <div className="absolute bottom-2 left-2 bg-black bg-opacity-70 text-white px-3 py-1 rounded text-sm">
            <FiImage className="inline mr-1" />
            Imazhi Gati
          </div>
        </div>
      )}
      {error && (
        <p className="text-red-500 text-sm mt-2">{error}</p>
      )}
    </div>
  );
};

export default ImageUpload;
